/**
 * MapView — provider-agnostic map abstraction.
 *
 * Backed by @vis.gl/react-google-maps (Google Maps Platform).
 * Previously backed by Leaflet + OpenStreetMap; migrated as part of #080.
 *
 * All consumers (CameraMapModal, DashboardPage, CamerasPage) remain unchanged —
 * the public MapMarker and MapViewProps interfaces are identical to the Leaflet era.
 *
 * MapMarker shape is intentionally provider-neutral.
 */

import { useEffect, useMemo, useState } from 'react'
import { Map as GoogleMap, AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { useMapConfig } from '@/hooks/useMapConfig'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MapMarker {
  id: number
  lat: number
  lng: number
  label: string        // camera name
  sublabel?: string    // location text
  groupNames?: string[]
  isOnline?: boolean | null   // tri-state: true (online) / false (offline) / null (unknown — typically FTP cameras with no heartbeat protocol)
  lastEventType?: string | null
  lastEventAt?: string | null
}

interface MapViewProps {
  markers: MapMarker[]
  /** Single-marker mode: fit map to this one pin. Multi-marker: fit bounds of all. */
  center?: { lat: number; lng: number }
  zoom?: number
  className?: string
  /** Called when a marker popup button is clicked (single-camera view) */
  onMarkerClick?: (id: number) => void
  /** Custom popup renderer — if provided, replaces the default popup content */
  renderPopup?: (marker: MapMarker) => React.ReactNode
}

// ── Spread overlapping markers ────────────────────────────────────────────────

// Spread markers that share the same coordinates so they're all visible
function spreadOverlapping(markers: MapMarker[]): MapMarker[] {
  const seen = new Map<string, number>()
  return markers.map(m => {
    const key = `${m.lat.toFixed(6)},${m.lng.toFixed(6)}`
    const count = seen.get(key) ?? 0
    seen.set(key, count + 1)
    if (count === 0) return m
    // Offset subsequent duplicates in a small circle (~30m radius)
    const angle = (count * 137.5 * Math.PI) / 180  // golden angle spread
    const offset = 0.0003
    return { ...m, lat: m.lat + offset * Math.sin(angle), lng: m.lng + offset * Math.cos(angle) }
  })
}

// ── Camera pin SVG ────────────────────────────────────────────────────────────

function CameraPinSvg({ isOnline }: { isOnline?: boolean | null }) {
  // Tri-state: emerald (online), rose (offline), slate-gray (unknown — FTP / no heartbeat)
  const fill = isOnline === true ? '#10b981' : isOnline === false ? '#f43f5e' : '#94a3b8'
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path
        d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"
        fill={fill}
        stroke="white"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="12" r="5" fill="white" opacity="0.9" />
    </svg>
  )
}

// ── Tooltip overlay ───────────────────────────────────────────────────────────

function MarkerTooltip({ marker }: { marker: MapMarker }) {
  const hasGroups = marker.groupNames && marker.groupNames.length > 0
  const hasEvent = !!marker.lastEventType
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 44,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '10px 14px',
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)',
        pointerEvents: 'none',
        zIndex: 50,
        minWidth: 200,
        maxWidth: 320,
        fontFamily: 'inherit',
      }}
    >
      {/* Online/offline status pill — tri-state. Hide entirely if isOnline is null/undefined
          (e.g., FTP cameras with no heartbeat — matches camera card behaviour). */}
      {marker.isOnline === true || marker.isOnline === false ? (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 8px',
            marginBottom: 8,
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            background: marker.isOnline ? '#d1fae5' : '#ffe4e6',
            color: marker.isOnline ? '#065f46' : '#9f1239',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: marker.isOnline ? '#10b981' : '#f43f5e',
            }}
          />
          {marker.isOnline ? 'Online' : 'Offline'}
        </div>
      ) : null}

      {/* Camera name */}
      <p
        style={{
          fontWeight: 700,
          fontSize: 15,
          lineHeight: 1.3,
          color: '#0f172a',
          margin: 0,
          marginBottom: marker.sublabel || hasGroups || hasEvent ? 6 : 0,
          wordBreak: 'break-word',
        }}
      >
        {marker.label}
      </p>

      {/* Location / address (sublabel) */}
      {marker.sublabel && (
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            color: '#475569',
            margin: 0,
            marginBottom: hasGroups || hasEvent ? 8 : 0,
            wordBreak: 'break-word',
          }}
        >
          {marker.sublabel}
        </p>
      )}

      {/* Group badges */}
      {hasGroups && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            marginBottom: hasEvent ? 8 : 0,
          }}
        >
          {marker.groupNames!.map((g, i) => (
            <span
              key={`${g}-${i}`}
              style={{
                fontSize: 11,
                fontWeight: 500,
                padding: '2px 7px',
                borderRadius: 4,
                background: '#f1f5f9',
                color: '#475569',
                whiteSpace: 'nowrap',
              }}
            >
              {g}
            </span>
          ))}
        </div>
      )}

      {/* Last event */}
      {hasEvent && (
        <p
          style={{
            fontSize: 11,
            color: '#64748b',
            margin: 0,
            paddingTop: 6,
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
            }}
          >
            Last
          </span>
          <span style={{ color: '#334155' }}>{marker.lastEventType}</span>
          {marker.lastEventAt && (
            <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
              {marker.lastEventAt}
            </span>
          )}
        </p>
      )}

      {/* Arrow pointing down to marker */}
      <span
        style={{
          position: 'absolute',
          bottom: -6,
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
          width: 12,
          height: 12,
          background: 'rgba(255, 255, 255, 0.98)',
          borderRight: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
          zIndex: -1,
        }}
      />
    </div>
  )
}

// ── Popup overlay ─────────────────────────────────────────────────────────────

function MarkerPopup({ marker, renderPopup }: { marker: MapMarker; renderPopup?: (m: MapMarker) => React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 40,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10,
        minWidth: 140,
        fontFamily: 'inherit',
      }}
    >
      {renderPopup ? renderPopup(marker) : (
        <div style={{ minWidth: 140 }}>
          <p style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, marginBottom: 2 }}>{marker.label}</p>
          {marker.sublabel && (
            <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{marker.sublabel}</p>
          )}
          {marker.groupNames && marker.groupNames.length > 0 && (
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{marker.groupNames.join(', ')}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Marker wrapper (handles hover tooltip vs click popup) ─────────────────────

function CameraMarker({
  marker,
  onMarkerClick,
  renderPopup,
}: {
  marker: MapMarker
  onMarkerClick?: (id: number) => void
  renderPopup?: (m: MapMarker) => React.ReactNode
}) {
  const [hovered, setHovered] = useState(false)
  const [popupOpen, setPopupOpen] = useState(false)

  function handleClick() {
    if (onMarkerClick) {
      onMarkerClick(marker.id)
    } else {
      setPopupOpen(open => !open)
    }
  }

  return (
    <AdvancedMarker
      position={{ lat: marker.lat, lng: marker.lng }}
      onClick={handleClick}
    >
      <div
        style={{ position: 'relative', cursor: 'pointer' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CameraPinSvg isOnline={marker.isOnline} />
        {hovered && !popupOpen && <MarkerTooltip marker={marker} />}
        {popupOpen && !onMarkerClick && (
          <MarkerPopup marker={marker} renderPopup={renderPopup} />
        )}
      </div>
    </AdvancedMarker>
  )
}

// ── Bounds fitter (must be a child of <Map> to use useMap) ───────────────────

function MapBoundsFitter({
  markers,
  explicitCenter,
}: {
  markers: MapMarker[]
  explicitCenter?: { lat: number; lng: number }
}) {
  const map = useMap()
  useEffect(() => {
    if (!map) return
    if (explicitCenter) return  // caller wants a fixed center — don't override
    if (markers.length === 0) return
    if (markers.length === 1) {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng })
      map.setZoom(15)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    for (const m of markers) {
      bounds.extend({ lat: m.lat, lng: m.lng })
    }
    map.fitBounds(bounds, 60)  // 60px padding
  }, [map, markers, explicitCenter])
  return null
}

// ── Main component ────────────────────────────────────────────────────────────

export function MapView({ markers, center, zoom, className = '', onMarkerClick, renderPopup }: MapViewProps) {
  const { data: config, isLoading } = useMapConfig()
  const spreadMarkers = useMemo(() => spreadOverlapping(markers), [markers])

  if (isLoading || !config) {
    return (
      <div
        className={className}
        style={{ minHeight: 300, background: '#f3f4f6', borderRadius: '0.5rem' }}
      />
    )
  }

  if (config.provider !== 'google') {
    return (
      <div
        className={className}
        style={{
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          color: '#6b7280',
          borderRadius: '0.5rem',
        }}
      >
        Map temporarily unavailable.
      </div>
    )
  }

  return (
    <GoogleMap
      className={className}
      style={{ width: '100%', height: '100%', minHeight: 400 }}
      defaultCenter={center ?? config.default_center}
      defaultZoom={zoom ?? config.default_zoom}
      mapTypeId="hybrid"
      mapId="DETERCAM_MAP"
      gestureHandling="auto"
      disableDefaultUI={false}
      mapTypeControl={true}
      streetViewControl={true}
      fullscreenControl={true}
      zoomControl={true}
      scaleControl={true}
      rotateControl={true}
      clickableIcons={true}
    >
      <MapBoundsFitter markers={spreadMarkers} explicitCenter={center} />
      {spreadMarkers.map(marker => (
        <CameraMarker
          key={marker.id}
          marker={marker}
          onMarkerClick={onMarkerClick}
          renderPopup={renderPopup}
        />
      ))}
    </GoogleMap>
  )
}
